import { useCallback, useEffect } from "react"

import {
    LayerProps,
    Layer as ReactMapLayer,
    useMap,
} from "react-map-gl/mapbox"

import { BASE_MAP_ID } from "../../constants/map"
import { useMapDispatchContext } from "../../contexts/MapContext"

export type L2RLayerProps = LayerProps & {
    id: string
    interactive?: boolean
    mapId?: string,
    onClick?: (event: mapboxgl.MapMouseEvent) => void
    onMouseEnter?: (event: mapboxgl.MapMouseEvent) => void
    onMouseLeave?: () => void
}

export const Layer = (props: L2RLayerProps) => {

    const {
        id,
        interactive = true,
        mapId = BASE_MAP_ID,
        onClick,
        onMouseEnter,
        onMouseLeave,
        ...layerProps
    } = props

    const { current: map } = useMap()
    const { addLayerClickListener, removeLayerClickListener } = useMapDispatchContext()

    const handleMouseEnter = useCallback((e: mapboxgl.MapMouseEvent) => {
        if (e.features?.[0]?.properties?.["disabled"]) return
        if (map) {
            map.getCanvas().style.cursor = "pointer"
        }
        if (onMouseEnter) {
            onMouseEnter(e)
        }
    }, [map, onMouseEnter])

    const handleMouseLeave = useCallback(() => {
        if (map) {
            map.getCanvas().style.cursor = ""
        }
        if (onMouseLeave) {
            onMouseLeave()
        }
    }, [map, onMouseLeave])

    useEffect(() => {
        if (!map || !onClick || !interactive) {
            return undefined
        }
        addLayerClickListener(id, onClick, mapId)
        map.on("mouseenter", id, handleMouseEnter)
        map.on("mouseleave", id, handleMouseLeave)

        return function cleanup() {
            removeLayerClickListener(id)
            map.off("mouseenter", id, handleMouseEnter)
            map.off("mouseleave", id, handleMouseLeave)
        }
    }, [handleMouseEnter,
        handleMouseLeave,
        map,
        mapId,
        id,
        interactive,
        onClick,
        onMouseEnter,
        onMouseLeave,
        addLayerClickListener,
        removeLayerClickListener])

    return <ReactMapLayer id={id} {...layerProps} />
}
